import React, { useCallback, useEffect, useState } from "react";
import {
	GOOGLE_AVERAGE_RATING,
	GOOGLE_REVIEWS_URL,
	GOOGLE_TOTAL_REVIEWS,
	googleReviews,
} from "../data/googleReviews";

const AUTOPLAY_MS = 6000;

const StarRating = ({ rating }) => (
	<div className="review-stars" aria-label={`${rating} out of 5 stars`}>
		{[1, 2, 3, 4, 5].map((star) => (
			<span
				key={star}
				className={`review-star${star <= rating ? " review-star-filled" : ""}`}
				aria-hidden="true"
			>
				★
			</span>
		))}
	</div>
);

const GoogleReviewsSlider = () => {
	const [activeIndex, setActiveIndex] = useState(0);
	const [isPaused, setIsPaused] = useState(false);
	const total = googleReviews.length;

	const goTo = useCallback(
		(index) => {
			setActiveIndex((index + total) % total);
		},
		[total]
	);

	const goNext = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);
	const goPrev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);

	useEffect(() => {
		if (isPaused || total <= 1) return;
		const timer = setInterval(goNext, AUTOPLAY_MS);
		return () => clearInterval(timer);
	}, [isPaused, goNext, total]);

	return (
		<section
			className="reviews-section"
			aria-label="Google customer reviews"
			onMouseEnter={() => setIsPaused(true)}
			onMouseLeave={() => setIsPaused(false)}
			onFocus={() => setIsPaused(true)}
			onBlur={() => setIsPaused(false)}
		>
			<div className="reviews-section-inner">
				<header className="reviews-header">
					<div className="reviews-google-badge">
						<svg
							className="reviews-google-icon"
							viewBox="0 0 24 24"
							aria-hidden="true"
						>
							<path
								fill="#4285F4"
								d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
							/>
							<path
								fill="#34A853"
								d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
							/>
							<path
								fill="#FBBC05"
								d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
							/>
							<path
								fill="#EA4335"
								d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
							/>
						</svg>
						<span>Google Reviews</span>
					</div>
					<h2 className="reviews-title">What our customers say</h2>
					<div className="reviews-summary">
						<StarRating rating={Math.round(GOOGLE_AVERAGE_RATING)} />
						<p className="reviews-summary-text">
							<strong>{GOOGLE_AVERAGE_RATING}</strong> · {GOOGLE_TOTAL_REVIEWS}{" "}
							reviews on Google
						</p>
					</div>
				</header>

				<div className="reviews-slider">
					<button
						type="button"
						className="reviews-nav reviews-nav-prev"
						onClick={goPrev}
						aria-label="Previous review"
					>
						‹
					</button>

					<div className="reviews-track-viewport">
						<div
							className="reviews-track"
							style={{ transform: `translateX(-${activeIndex * 100}%)` }}
						>
							{googleReviews.map((review) => (
								<article key={review.id} className="review-card">
									<div className="review-card-top">
										<div className="review-author-avatar" aria-hidden="true">
											{review.author.charAt(0)}
										</div>
										<div>
											<p className="review-author">{review.author}</p>
											<p className="review-date">{review.date}</p>
										</div>
									</div>
									<StarRating rating={review.rating} />
									{review.text ? (
										<>
											<p className="review-text">&ldquo;{review.text}&rdquo;</p>
											{review.textOriginal && (
												<p className="review-text-original" lang="bg">
													{review.textOriginal}
												</p>
											)}
										</>
									) : (
										<p className="review-text review-text-rating-only">
											Rated {review.rating} stars on Google
										</p>
									)}
									<p className="review-source">Posted on Google</p>
								</article>
							))}
						</div>
					</div>

					<button
						type="button"
						className="reviews-nav reviews-nav-next"
						onClick={goNext}
						aria-label="Next review"
					>
						›
					</button>
				</div>

				<div className="reviews-dots" role="tablist" aria-label="Review slides">
					{googleReviews.map((review, index) => (
						<button
							key={review.id}
							type="button"
							role="tab"
							aria-selected={index === activeIndex}
							aria-label={`Go to review ${index + 1}`}
							className={`reviews-dot${
								index === activeIndex ? " reviews-dot-active" : ""
							}`}
							onClick={() => goTo(index)}
						/>
					))}
				</div>

				<a
					href={GOOGLE_REVIEWS_URL}
					target="_blank"
					rel="noopener noreferrer"
					className="reviews-google-link"
				>
					View all reviews on Google →
				</a>
			</div>
		</section>
	);
};

export default GoogleReviewsSlider;
